import { useState, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useIntl } from "react-intl";

interface ICoverageBadgeProps {
  isCovered: boolean;
  notCoveredReason?: string;
  availableIn?: string;
}

export const CoverageBadge = (props: ICoverageBadgeProps) => {
  const intl = useIntl();

  const COVERED_TOOLTIP = intl.formatMessage({ id: 'intl-msg:budgets.coveredTooltip' });
  const NOT_COVERED_TOOLTIP = intl.formatMessage({ id: 'intl-msg:budgets.notCoveredTooltip' });
  const TOOLTIP_GAP = 8;

  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<{ top: number; left: number } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const baseClasses =
    "w-full min-w-0 max-w-full h-[25px] rounded text-bold text-size-10 flex px-5 py-2 items-center justify-center text-[10px] font-medium text-white uppercase leading-none cursor-default overflow-hidden";

  const renderBadgeLabel = (label: string) => (
    <span className="block min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-center">
      {label}
    </span>
  );

  const tooltipText = props.isCovered
    ? COVERED_TOOLTIP
    : (props.notCoveredReason?.trim() || NOT_COVERED_TOOLTIP);

  useLayoutEffect(() => {
    if (!showTooltip || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    setTooltipStyle({
      top: rect.top + rect.height / 2,
      left: rect.right + TOOLTIP_GAP,
    });
  }, [showTooltip]);

  const getBadge = () => {
    if (!props.isCovered && props.availableIn) {
      return (
        <div className={`${baseClasses} bg-orange-500`}>
          {renderBadgeLabel(intl.formatMessage({ id: 'intl-msg:budgets.inGracePeriod' }))}
        </div>
      )
    }

    if(props.isCovered) {
      return (
        <div className={`${baseClasses} bg-green-600`}>
          {renderBadgeLabel(intl.formatMessage({ id: 'intl-msg:budgets.covered' }))}
        </div>
      )
    }

    return (
      <div className={`${baseClasses} bg-red-600`}>
        {renderBadgeLabel(intl.formatMessage({ id: 'intl-msg:budgets.notCovered' }))}
      </div>
    )
  }

  const tooltipEl =
    showTooltip &&
    tooltipStyle &&
    createPortal(
      <div
        className="coverage-badge-tooltip fixed z-[99999] px-3 py-2 rounded bg-black text-white text-size-12 text-center max-w-[220px] shadow-lg whitespace-normal -translate-y-1/2"
        style={{ top: tooltipStyle.top, left: tooltipStyle.left }}
        role="tooltip"
      >
        {tooltipText}
        <span
          className="absolute right-full top-1/2 -translate-y-1/2 border-[6px] border-transparent border-r-black"
          style={{ marginRight: "-1px" }}
        />
      </div>,
      document.body
    );

  return (
    <div
      ref={wrapperRef}
      className="relative block w-full min-w-0 max-w-full"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {getBadge()}
      {tooltipEl}
    </div>
  );
};